/**
 * Remarketing & Audience Segmentation
 *
 * Pushes user engagement signals to dataLayer for GA4 audiences
 * and Google Ads remarketing. Also fires FB custom audiences events.
 */
import facebookPixel from '../services/facebookPixel';

const ENGAGEMENT_KEY = 'sp_engagement';

/**
 * Get stored engagement data
 */
function getEngagement() {
  try {
    return JSON.parse(localStorage.getItem(ENGAGEMENT_KEY) || '{}');
  } catch { return {}; }
}

/**
 * Save engagement data
 */
function saveEngagement(data) {
  try {
    localStorage.setItem(ENGAGEMENT_KEY, JSON.stringify({ ...getEngagement(), ...data }));
  } catch { /* ignore */ }
}

/**
 * Initialize remarketing tracking on page load.
 * Call this once in App.js.
 */
export function initRemarketing() {
  if (typeof window === 'undefined') return;

  const engagement = getEngagement();
  const visitCount = (engagement.visits || 0) + 1;
  const firstVisit = engagement.firstVisit || Date.now();
  const daysSinceFirst = Math.floor((Date.now() - firstVisit) / (1000 * 60 * 60 * 24));

  saveEngagement({
    visits: visitCount,
    firstVisit,
    lastVisit: Date.now(),
  });

  // Push user segment to dataLayer for GA4 audiences
  const segment = visitCount === 1 ? 'new' : visitCount <= 3 ? 'returning' : 'engaged';
  window.dataLayer?.push({
    event: 'user_segment',
    user_segment: segment,
    visit_count: visitCount,
    days_since_first: daysSinceFirst,
  });

  // GA4 user properties for audience building
  if (typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', {
      engagement_level: segment,
      visit_count: visitCount,
      days_since_first_visit: daysSinceFirst,
    });
  }
}

/**
 * Track scroll depth (25%, 50%, 75%, 100%).
 * Call this in landing page components.
 */
export function trackScrollDepth() {
  if (typeof window === 'undefined') return;

  const milestones = [25, 50, 75, 100];
  const fired = new Set();

  const handler = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const percent = Math.round((scrollTop / docHeight) * 100);

    milestones.forEach(m => {
      if (percent >= m && !fired.has(m)) {
        fired.add(m);
        window.dataLayer?.push({
          event: 'scroll_depth',
          scroll_percent: m,
          page_path: window.location.pathname,
        });

        // Deep scroll = high intent signal
        if (m >= 75) {
          facebookPixel.trackCustom('DeepScroll', {
            scroll_percent: m,
            page_path: window.location.pathname,
          });
        }
      }
    });
  };

  window.addEventListener('scroll', handler, { passive: true });
  return () => window.removeEventListener('scroll', handler);
}

/**
 * Track time on page (10s, 30s, 60s, 120s).
 * Returns cleanup function.
 */
export function trackTimeOnPage() {
  if (typeof window === 'undefined') return () => {};

  const milestones = [10, 30, 60, 120];
  const timers = milestones.map(seconds =>
    setTimeout(() => {
      window.dataLayer?.push({
        event: 'time_on_page',
        time_seconds: seconds,
        page_path: window.location.pathname,
      });

      // 60s+ = engaged user
      if (seconds >= 60) {
        facebookPixel.trackCustom('EngagedUser', {
          time_seconds: seconds,
          page_path: window.location.pathname,
        });
      }
    }, seconds * 1000)
  );

  return () => timers.forEach(clearTimeout);
}

/**
 * Track pricing page view - high intent signal.
 */
export function trackPricingView(packageName, price) {
  window.dataLayer?.push({
    event: 'view_pricing',
    package_name: packageName,
    price_value: price,
  });

  facebookPixel.trackViewContent({
    content_name: packageName,
    content_category: 'pricing',
    value: price,
    currency: 'PLN',
  });

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'view_item', {
      currency: 'PLN',
      value: price,
      items: [{ item_name: packageName, price }],
    });
  }
}

/**
 * Track teacher profile view - interest signal.
 */
export function trackTeacherView(teacherId, teacherName) {
  window.dataLayer?.push({
    event: 'view_teacher',
    teacher_id: teacherId,
    teacher_name: teacherName,
  });

  facebookPixel.trackViewContent({
    content_name: teacherName,
    content_category: 'teacher_profile',
    content_ids: [teacherId],
  });
}
